import { useEffect, useState } from "react";
import { ThemeMode } from "@sscs/types";
import { Toast } from "@sscs/ui";
import { RECAPTCHA_SCRIPT_URL } from "Constants";
import { useScript } from "Hooks";
import { getBrowserWindow, getDocNonce } from "Utils";

type UseRecaptchaProps = {
  sitekey: string;
  containerId: string;
  // The reCAPTCHA widget is a cross-origin Google iframe we can't restyle with
  // our tokens; its `theme` param ("light" | "dark") is the only lever, so we
  // feed it the app's current mode. It reads this once at render (Google won't
  // re-theme a mounted widget), which is fine for the login/reset flow where the
  // theme is fixed before this page mounts.
  theme: ThemeMode;
};

type UseRecaptchaHookReturn = {
  reCaptchaResponseToken?: string | null;
  widgetRendered: boolean;
};

type UseRecaptcha = (options: UseRecaptchaProps) => UseRecaptchaHookReturn;

const ERROR_SCRIPT_NOT_AVAILABLE = "Google recaptcha is not available";

export const useRecaptcha: UseRecaptcha = ({ sitekey, containerId, theme }) => {
  const [token, setToken] = useState<string | null>(null);
  const [recaptchaRendered, setRecaptchaRendered] = useState(false);
  const [recaptchaWidget, setRecaptchaWidget] = useState<unknown | null>(null);

  // as per the docs, this should mean that nonce is OK for the
  // recaptcha component
  window.recaptchaOptions = {
    nonce: getDocNonce(),
  };

  // inject the script into the document head
  // this will automatically take care of making sure we
  // don't try render it more than once.
  const scriptStatus = useScript({ src: RECAPTCHA_SCRIPT_URL });

  // recaptcha is a little funny in that you have to call it
  // and then wait for it's ready method to be avilable before
  // passing it your own callback to render it
  useEffect(() => {
    // when we have any error from the recaptca
    // reset the token so it can't be submitted
    const onReCaptchaError = () => {
      setToken(null);
    };

    // conversely, if the recaptcha is executed successfully, store the token
    const onExecuteReCaptcha = (response: string) => {
      setToken(response);
    };

    // resetting will happen if the recaptcha is allowed to
    // timeout; in this case we reset the token state
    const onResetReCaptcha = () => {
      const window = getBrowserWindow();
      if (!window) {
        return;
      }

      const { grecaptcha } = window;
      if (!grecaptcha) {
        Toast.show({
          title: ERROR_SCRIPT_NOT_AVAILABLE,
          type: "error",
        });
      }

      try {
        const { getResponse, reset } = grecaptcha;
        if (getResponse()) {
          reset();
          setToken(null);
        }
      } catch (error) {
        console.error(error); //eslint-disable-line no-console
      }
    };

    // this is responsible for finally rendering the recaptcha component
    // to the screen and setting all the callbacks up
    const renderReCaptcha = () => {
      const widget =
        window.grecaptcha &&
        window.grecaptcha.render(containerId, {
          sitekey,
          size: "normal",
          theme,
          callback: onExecuteReCaptcha,
          "expired-callback": onResetReCaptcha,
          "error-callback": onReCaptchaError,
        });
      setRecaptchaWidget(widget);
      setRecaptchaRendered(true);
    };

    if (
      recaptchaWidget === null &&
      scriptStatus === "ready" &&
      window.grecaptcha !== undefined
    ) {
      window.grecaptcha.ready(() => {
        if (!recaptchaRendered) renderReCaptcha();
      });
    }
  }, [
    containerId,
    recaptchaRendered,
    recaptchaWidget,
    scriptStatus,
    sitekey,
    theme,
  ]);

  return {
    reCaptchaResponseToken: token,
    widgetRendered: recaptchaWidget !== null,
  };
};

export default useRecaptcha;
